// The playable scale units, their scale slugs and their permanent level numbers.
//
// Every catalog unit outside the Bebop family is playable: 80 units. The six
// Bebop scales are eight-note passing-tone scales, and the scale drill's played
// format (direction, fingering) does not fit them yet, so they are listed in the
// catalog but mint no content.
//
// A unit's scale slug is its catalog id, with one exception: Ionian / Major is
// `major`, the slug the backend's scale row and every stored cell already use
// (a slug is permanent once rows reference it).
//
// A level number is the unit's permanent authoring identity in the
// scales-performance-read exercise: assigned once, when the unit's level is first
// written, and never changed or reused, because tier completions point at it.
// Major keeps level 1; the other 79 took 2-80 in catalog order when the library
// was first filled. A unit added later takes the next unused number, wherever it
// sits in the catalog. Display order never comes from these numbers: it is the
// catalog's family and within-family order (`PLAYABLE_SCALE_UNIT_IDS`).

import { SCALE_FAMILY_LISTINGS } from './scaleLibraryCatalog';

/** Families listed in the catalog whose units mint no playable content. */
export const UNPLAYABLE_SCALE_FAMILIES: readonly string[] = ['bebop'];

/** Scale slug -> level number. Append only: never renumber, never reuse a number. */
export const SCALE_LEVEL_NUMBERS = {
  major: 1,
  dorian: 2,
  phrygian: 3,
  lydian: 4,
  mixolydian: 5,
  aeolian: 6,
  locrian: 7,
  'ascending-melodic-minor': 8,
  'phrygian-nat6': 9,
  'lydian-augmented': 10,
  'lydian-dominant': 11,
  'aeolian-dominant': 12,
  'locrian-nat2': 13,
  superlocrian: 14,
  'harmonic-minor': 15,
  'locrian-nat6': 16,
  'ionian-augmented': 17,
  romanian: 18,
  'phrygian-dominant': 19,
  'lydian-2': 20,
  ultralocrian: 21,
  'harmonic-major': 22,
  'dorian-b5': 23,
  'phrygian-b4': 24,
  'lydian-b3': 25,
  'mixolydian-b2': 26,
  'lydian-augmented-2': 27,
  'locrian-bb7': 28,
  'fully-diminished': 29,
  'dominant-diminished': 30,
  'minor-pentatonic': 31,
  'major-pentatonic': 32,
  'egyptian-suspended': 33,
  'blues-minor-pentatonic': 34,
  'blues-major-pentatonic': 35,
  'whole-tone': 36,
  augmented: 37,
  'inverted-augmented': 38,
  prometheus: 39,
  tritone: 40,
  hexatonic: 41,
  'major-blues': 42,
  'nonatonic-blues': 43,
  'decatonic-blues': 44,
  'double-harmonic-major': 45,
  'lydian-2-6': 46,
  ultraphrygian: 47,
  'hungarian-minor': 48,
  oriental: 49,
  'ionian-2-5': 50,
  'locrian-bb3-bb7': 51,
  'neapolitan-major': 52,
  'leading-whole-tone': 53,
  'lydian-dominant-augmented': 54,
  'lydian-minor': 55,
  'major-locrian': 56,
  'altered-dominant-major-2nd': 57,
  'altered-dominant-diminished-3rd': 58,
  'neapolitan-minor': 59,
  'lydian-6': 60,
  'mixolydian-augmented': 61,
  'hungarian-gypsy': 62,
  'locrian-dominant': 63,
  'ionian-2': 64,
  'ultralocrian-bb3': 65,
  'hungarian-major': 66,
  'ultralocrian-bb6': 67,
  'harmonic-minor-b5': 68,
  'superlocrian-nat6': 69,
  'jazz-minor-5': 70,
  'dorian-b2-4': 71,
  'nohkan-flute-scale': 72,
  'romanian-major': 73,
  'super-lydian-augmentednat6': 74,
  'locriannat2-bb7': 75,
  'superlocrian-bb6': 76,
  'jazz-minor-b5': 77,
  'javanese-b4': 78,
  'lydian-augmented-b3': 79,
  chromatic: 80,
} as const;

/** A playable scale's slug: the `scale` value of its cells. */
export type ScaleSlug = keyof typeof SCALE_LEVEL_NUMBERS;

/** The one unit whose slug is not its catalog id. */
const SLUG_BY_UNIT_ID: Readonly<Record<string, ScaleSlug>> = { ionian: 'major' };
const UNIT_ID_BY_SLUG: Readonly<Record<string, string>> = { major: 'ionian' };

/** The playable units in display order: catalog family order, then within-family
 *  order; a unit listed in two families appears once, at its first listing. */
export const PLAYABLE_SCALE_UNIT_IDS: readonly string[] = SCALE_FAMILY_LISTINGS.filter(
  (listing) => !UNPLAYABLE_SCALE_FAMILIES.includes(listing.family),
)
  .flatMap((listing) => listing.unitIds)
  .filter((unitId, index, unitIds) => unitIds.indexOf(unitId) === index);

const isScaleSlug = (slug: string): slug is ScaleSlug =>
  Object.prototype.hasOwnProperty.call(SCALE_LEVEL_NUMBERS, slug);

/** The playable unit's scale slug; throws for a unit that is not playable. */
export const scaleSlugForUnit = (unitId: string): ScaleSlug => {
  const slug = SLUG_BY_UNIT_ID[unitId] ?? unitId;
  if (!isScaleSlug(slug) || !PLAYABLE_SCALE_UNIT_IDS.includes(unitId)) {
    throw new Error(`scale levels: "${unitId}" is not a playable scale unit`);
  }
  return slug;
};

/** The catalog unit id behind a scale slug. */
export const unitIdForScaleSlug = (slug: ScaleSlug): string => UNIT_ID_BY_SLUG[slug] ?? slug;
